/**
 * PermissionGridHeader Component
 * Role column header with optional edit/disable actions
 * Click role name to edit, Disable button to soft-delete
 */

import React, { memo } from 'react';
import { colors } from '../../theme';

export interface RoleColumnInfo {
  id: string;
  name: string;
  roleType: 'account' | 'location';
  userCount?: number;
  isDefault?: boolean;
  isSystem?: boolean;
  isDeleted?: boolean;
}

export interface PermissionGridHeaderProps {
  role: RoleColumnInfo;
  onEdit?: (roleId: string) => void;
  onDisable?: (roleId: string) => void;
  onEnable?: (roleId: string) => void;
  canEdit?: boolean;
  minWidth?: number;
}

/**
 * Header cell for a role column in the permission grid
 */
export const PermissionGridHeader = memo(function PermissionGridHeader({
  role,
  onEdit,
  onDisable,
  onEnable,
  canEdit = true,
  minWidth = 36,
}: PermissionGridHeaderProps) {
  const canClickToEdit = canEdit && !role.isSystem && !role.isDeleted && onEdit;
  const showDisableButton = canEdit && !role.isSystem && !role.isDeleted && onDisable;
  const showEnableButton = canEdit && !role.isSystem && role.isDeleted && onEnable;

  return (
    <th
      style={{
        position: 'sticky',
        top: 0,
        backgroundColor: colors.background.secondary,
        padding: '4px 1px',
        textAlign: 'center',
        borderBottom: `1px solid ${colors.border.default}`,
        borderRight: `1px solid ${colors.border.default}`,
        minWidth: `${minWidth}px`,
        maxWidth: `${minWidth + 10}px`,
        zIndex: 2,
        verticalAlign: 'top',
        height: '120px',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: '4px',
          height: '100%',
          paddingTop: '6px',
        }}
      >
        {/* Vertical role name */}
        <div
          style={{
            writingMode: 'vertical-rl',
            maxHeight: '90px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          <span
            onClick={canClickToEdit ? () => onEdit(role.id) : undefined}
            style={{
              fontSize: '12px',
              fontWeight: 600,
              color: colors.text.primary,
              cursor: canClickToEdit ? 'pointer' : 'default',
              transition: 'color 0.15s ease',
            }}
            onMouseOver={
              canClickToEdit
                ? (e) => {
                    e.currentTarget.style.color = colors.brand.primary;
                  }
                : undefined
            }
            onMouseOut={
              canClickToEdit
                ? (e) => {
                    e.currentTarget.style.color = colors.text.primary;
                  }
                : undefined
            }
            title={canClickToEdit ? `Click to edit ${role.name}` : role.name}
          >
            {role.name}
          </span>
        </div>

        {/* User count and default badge */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '2px',
          }}
        >
          {role.userCount !== undefined && (
            <span
              style={{
                fontSize: '10px',
                color: colors.text.secondary,
              }}
            >
              {role.userCount}
            </span>
          )}

          {role.isDefault && (
            <span
              style={{
                fontSize: '9px',
                color: colors.brand.primary,
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                padding: '1px 4px',
                borderRadius: '3px',
              }}
            >
              Def
            </span>
          )}

          {role.isDeleted && (
            <span
              style={{
                fontSize: '9px',
                color: '#ef4444',
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                padding: '1px 4px',
                borderRadius: '3px',
              }}
            >
              Off
            </span>
          )}

          {showEnableButton && (
            <button
              onClick={() => onEnable(role.id)}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '1px 4px',
                fontSize: '9px',
                color: '#22c55e',
                borderRadius: '3px',
                transition: 'all 0.15s ease',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(34, 197, 94, 0.1)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              title="Enable role"
            >
              Enable
            </button>
          )}

          {showDisableButton && (
            <button
              onClick={() => onDisable(role.id)}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '1px 4px',
                fontSize: '9px',
                color: colors.text.tertiary,
                borderRadius: '3px',
                transition: 'all 0.15s ease',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
                e.currentTarget.style.color = '#ef4444';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = colors.text.tertiary;
              }}
              title="Disable role"
            >
              ✕
            </button>
          )}
        </div>
      </div>
    </th>
  );
});

export default PermissionGridHeader;
